import { IRobotOption, RobotSeries } from '../models/robot-selector.model';
import { IPhysicalRequirement, ratedPayloadKg } from './eligibility';

export interface IRankedRobot {
    robot: IRobotOption;
    rank: number;
    isBestMatch: boolean;
}

// Newest range first, so identical specs never fall back to an arbitrary order.
const SERIES_ORDER: Record<RobotSeries, number> = {
    'g-series': 0,
    'ur-series': 1,
    'e-series': 2,
};

type Comparator = (left: IRobotOption, right: IRobotOption) => number;

function fit(robot: IRobotOption, requirement: IPhysicalRequirement): number {
    const slackPayload = (ratedPayloadKg(robot) - requirement.payloadKg) / requirement.payloadKg;
    const slackReach = (robot.reachMm - requirement.reachMm) / requirement.reachMm;

    return slackPayload + slackReach;
}

const bySize: Comparator = (left, right) =>
    ratedPayloadKg(left) - ratedPayloadKg(right) || left.reachMm - right.reachMm;

const bySeries: Comparator = (left, right) => SERIES_ORDER[left.series] - SERIES_ORDER[right.series];

const byIdentity: Comparator = (left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id);

export function toRanked(robots: readonly IRobotOption[]): IRankedRobot[] {
    return robots.map((robot, index) => ({ robot, rank: index + 1, isBestMatch: index === 0 }));
}

/** Closest fit first: the robot that overshoots the requirement least. */
export function rank(robots: readonly IRobotOption[], requirement: IPhysicalRequirement): IRankedRobot[] {
    return toRanked(
        [...robots].sort(
            (left, right) =>
                fit(left, requirement) - fit(right, requirement) ||
                bySize(left, right) ||
                bySeries(left, right) ||
                byIdentity(left, right),
        ),
    );
}

/** The landing order before any filter is touched: the g-Series lead, then the rest of the portfolio. */
export function featured(robots: readonly IRobotOption[]): IRankedRobot[] {
    const leads = (robot: IRobotOption) => (robot.series === 'g-series' ? 0 : 1);

    return toRanked(
        [...robots].sort(
            (left, right) =>
                leads(left) - leads(right) || bySize(left, right) || bySeries(left, right) || byIdentity(left, right),
        ),
    );
}
